/**
 * Shared filter functions for Material React Table columns.
 *
 * These live in one place because persisted table state round-trips through
 * JSON, and JSON has no Date type. A saved date-range filter comes back as a
 * pair of ISO strings, and in JavaScript comparing a Date against a string is
 * always false (the string is coerced with Number(), giving NaN). A filter
 * restored from storage would therefore hide every row — an empty table with
 * the filter still showing as active and nothing in the console.
 *
 * Everything is coerced to a real Date before being compared, so the same
 * function works whether the value came straight from the date picker or from
 * localStorage.
 */

/**
 * Coerce a filter bound or cell value to a Date.
 * @returns a valid Date, or null for empty/unparseable input.
 */
const toDate = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Auto-correct a reversed range so [later, earlier] still matches, rather
 * than matching nothing.
 */
export const validateDateRange = (filterValue) => {
    if (!filterValue || !Array.isArray(filterValue)) return filterValue;

    const start = toDate(filterValue[0]);
    const end = toDate(filterValue[1]);

    if (start && end && end < start) return [end, start];
    return [start, end];
};

/**
 * Inclusive date-range filter. Rows with no date are always kept, matching
 * the behaviour these tables had before this was centralised.
 */
export const dateRangeFilterFn = (row, columnId, filterValue) => {
    const date = toDate(row.getValue(columnId));

    if (!date) return true;

    if (!filterValue) return true;

    const [start, end] = validateDateRange(filterValue);

    if (start && !end) return date >= start;
    if (!start && end) return date <= end;
    if (start && end) return date >= start && date <= end;

    return true;
};

export default { dateRangeFilterFn, validateDateRange, toDate };
